import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { weekLabel, volledigeDatum } from '../utils/datum';

const STATUS_NL = {
  gepland: 'Gepland', in_uitvoering: 'In uitvoering', on_hold: 'On hold',
  afgewerkt: 'Afgewerkt', voltooid: 'Voltooid', geannuleerd: 'Geannuleerd',
};
const STATUS_BG = {
  gepland: '#E6F1FB', in_uitvoering: '#EAF3DE', on_hold: '#FFF2CC',
  afgewerkt: '#EAF3DE', voltooid: '#F1EFE8', geannuleerd: '#FCEBEB',
};
const STATUS_CL = {
  gepland: '#0C447C', in_uitvoering: '#27500A', on_hold: '#7F6000',
  afgewerkt: '#1A7A3C', voltooid: '#444', geannuleerd: '#791F1F',
};

const AFGEWERKT_STATUSSEN = ['afgewerkt', 'voltooid', 'geannuleerd'];
const MAT_BG = { in_stock: '#EAF3DE', te_bestellen: '#FDEDEC', besteld: '#FDF3E3', geleverd: '#E6F1FB' };
const MAT_CL = { in_stock: '#1A7A3C', te_bestellen: '#C0392B', besteld: '#B5660E', geleverd: '#185FA5' };
const MAT_NL = { in_stock: 'In stock', te_bestellen: 'Te bestellen', besteld: 'Besteld', geleverd: 'Geleverd' };
const PRIO_CL = { spoed: '#A32D2D', hoog: '#854F0B', normaal: '#3B6D11' };
const PRIO_RANG = { spoed: 0, hoog: 1, normaal: 2 };

const STATUS_TABS = [
  { key: 'actief',    label: 'Actief' },
  { key: 'te_laat',  label: 'Te laat' },
  { key: 'voltooid', label: 'Voltooid' },
  { key: 'alle',     label: 'Alle' },
];

const PRIO_CHIPS = [
  { key: '', label: 'Alle' },
  { key: 'spoed', label: 'Spoed' },
  { key: 'hoog', label: 'Hoog' },
  { key: 'normaal', label: 'Normaal' },
];

const MAT_CHIPS = [
  { key: '', label: 'Alle' },
  { key: 'te_bestellen', label: 'Te bestellen' },
  { key: 'besteld', label: 'Besteld' },
  { key: 'geleverd', label: 'Geleverd' },
  { key: 'in_stock', label: 'In stock' },
];

const KOLOMMEN = [
  { key: 'ordernummer', label: 'Order nr.' },
  { key: 'klant',       label: 'Klant' },
  { key: 'artikel',     label: 'Artikel' },
  { key: 'aantal',      label: 'Aant.' },
  { key: 'leverdatum',  label: 'Leverdatum' },
  { key: 'dagen',       label: 'Dagen' },
  { key: 'prioriteit',  label: 'Prio' },
  { key: 'status',      label: 'Status' },
  { key: 'materiaal',   label: 'Materiaal' },
  { key: 'voortgang',   label: 'Voortgang' },
];

function Chip({ active, onClick, children, color }) {
  return (
    <button onClick={onClick} style={{
      padding: '4px 12px', borderRadius: 99, fontSize: 12, cursor: 'pointer',
      border: active ? `1.5px solid ${color || '#185FA5'}` : '1px solid #DDD',
      background: active ? (color ? color + '18' : '#E6F1FB') : '#fff',
      color: active ? (color || '#185FA5') : '#555',
      fontWeight: active ? 600 : 400,
    }}>
      {children}
    </button>
  );
}

export default function OrdersPage({ user }) {
  const [orders, setOrders] = useState([]);
  const [laden, setLaden] = useState(true);
  const [zoek, setZoek] = useState('');
  const [statusFilter, setStatusFilter] = useState('actief');
  const [prioriteitFilter, setPrioriteitFilter] = useState('');
  const [materiaalFilter, setMateriaalFilter] = useState('');
  const [sortKolom, setSortKolom] = useState('dagen');
  const [sortAsc, setSortAsc] = useState(true);
  const nav = useNavigate();

  const isPlanner = ['planner', 'admin'].includes(user?.rol);

  useEffect(() => {
    api.get('/orders').then(r => { setOrders(r.data); setLaden(false); });
  }, []);

  const wisFilters = () => {
    setZoek('');
    setStatusFilter('actief');
    setPrioriteitFilter('');
    setMateriaalFilter('');
  };

  const actieveFilters = [prioriteitFilter, materiaalFilter, zoek].filter(Boolean).length;

  const klikKolom = (key) => {
    if (sortKolom === key) setSortAsc(a => !a);
    else { setSortKolom(key); setSortAsc(true); }
  };

  const gefilterd = useMemo(() => {
    let lijst = orders.filter(o => {
      if (statusFilter === 'actief'   && AFGEWERKT_STATUSSEN.includes(o.status)) return false;
      if (statusFilter === 'te_laat'  && (o.dagen_resterend >= 0 || AFGEWERKT_STATUSSEN.includes(o.status))) return false;
      if (statusFilter === 'voltooid' && !AFGEWERKT_STATUSSEN.includes(o.status)) return false;
      if (prioriteitFilter && o.prioriteit !== prioriteitFilter) return false;
      if (materiaalFilter  && o.materiaal_status !== materiaalFilter) return false;
      if (zoek) {
        const q = zoek.toLowerCase();
        const hay = `${o.ordernummer} ${o.klant} ${o.artikel_omschrijving} ${o.tekeningnummer || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });

    lijst.sort((a, b) => {
      let va, vb;
      switch (sortKolom) {
        case 'ordernummer': va = a.ordernummer; vb = b.ordernummer; break;
        case 'klant':       va = a.klant;       vb = b.klant;       break;
        case 'artikel':     va = a.artikel_omschrijving; vb = b.artikel_omschrijving; break;
        case 'aantal':      va = a.aantal;      vb = b.aantal;      break;
        case 'leverdatum':  va = a.leverdatum;  vb = b.leverdatum;  break;
        case 'dagen':       va = parseInt(a.dagen_resterend); vb = parseInt(b.dagen_resterend); break;
        case 'prioriteit':  va = PRIO_RANG[a.prioriteit] ?? 9; vb = PRIO_RANG[b.prioriteit] ?? 9; break;
        case 'status':      va = a.status;      vb = b.status;      break;
        case 'materiaal':   va = a.materiaal_status; vb = b.materiaal_status; break;
        case 'voortgang':
          va = a.totaal_stappen > 0 ? a.voltooide_stappen / a.totaal_stappen : 0;
          vb = b.totaal_stappen > 0 ? b.voltooide_stappen / b.totaal_stappen : 0;
          break;
        default: va = 0; vb = 0;
      }
      if (va < vb) return sortAsc ? -1 : 1;
      if (va > vb) return sortAsc ? 1 : -1;
      return 0;
    });

    return lijst;
  }, [orders, statusFilter, prioriteitFilter, materiaalFilter, zoek, sortKolom, sortAsc]);

  const SortPijl = ({ kolom }) => {
    if (sortKolom !== kolom) return <span style={{ opacity: 0.3, marginLeft: 4 }}>↕</span>;
    return <span style={{ marginLeft: 4 }}>{sortAsc ? '↑' : '↓'}</span>;
  };

  return (
    <div style={{ padding: 24 }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h1 style={{ margin: 0, fontSize: 20, fontWeight: 600, color: '#1F3864' }}>Orders</h1>
        {isPlanner && (
          <button onClick={() => nav('/orders/nieuw')}
            style={{ padding: '8px 16px', background: '#185FA5', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 500, cursor: 'pointer' }}>
            + Nieuw order
          </button>
        )}
      </div>

      {/* Zoek + status tabs */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          value={zoek} onChange={e => setZoek(e.target.value)}
          placeholder="Zoeken op order, klant, artikel, tekeningnr..."
          style={{ flex: 1, minWidth: 220, padding: '7px 12px', border: '1px solid #DDD', borderRadius: 6, fontSize: 13 }}
        />
        <div style={{ display: 'flex', gap: 6 }}>
          {STATUS_TABS.map(t => (
            <button key={t.key} onClick={() => setStatusFilter(t.key)} style={{
              padding: '7px 14px', border: '1px solid #DDD', borderRadius: 6, fontSize: 12, cursor: 'pointer',
              background: statusFilter === t.key ? '#1F3864' : '#fff',
              color: statusFilter === t.key ? '#fff' : '#555',
              fontWeight: statusFilter === t.key ? 600 : 400,
            }}>{t.label}</button>
          ))}
        </div>
      </div>

      {/* Extra filter chips */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 11, color: '#888', whiteSpace: 'nowrap' }}>Prioriteit:</span>
          {PRIO_CHIPS.map(c => (
            <Chip key={c.key} active={prioriteitFilter === c.key} color={c.key ? PRIO_CL[c.key] : undefined}
              onClick={() => setPrioriteitFilter(c.key)}>{c.label}</Chip>
          ))}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 11, color: '#888', whiteSpace: 'nowrap' }}>Materiaal:</span>
          {MAT_CHIPS.map(c => (
            <Chip key={c.key} active={materiaalFilter === c.key} color={c.key ? MAT_CL[c.key] : undefined}
              onClick={() => setMateriaalFilter(c.key)}>{c.label}</Chip>
          ))}
        </div>
        {actieveFilters > 0 && (
          <button onClick={wisFilters} style={{
            marginLeft: 'auto', fontSize: 12, color: '#888', background: 'none',
            border: '1px solid #DDD', borderRadius: 6, padding: '4px 12px', cursor: 'pointer',
          }}>
            ✕ Filters wissen ({actieveFilters})
          </button>
        )}
      </div>

      {/* Tabel */}
      <div style={{ background: '#fff', border: '1px solid #E0E0E0', borderRadius: 8, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#1F3864', color: '#fff' }}>
              {KOLOMMEN.map(k => (
                <th key={k.key} onClick={() => klikKolom(k.key)} style={{
                  padding: '10px 12px', textAlign: 'left', fontWeight: 500, fontSize: 12,
                  cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap',
                }}>
                  {k.label}<SortPijl kolom={k.key} />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {laden ? (
              <tr><td colSpan={10} style={{ padding: 24, color: '#888', textAlign: 'center' }}>Laden...</td></tr>
            ) : gefilterd.length === 0 ? (
              <tr><td colSpan={10} style={{ padding: 24, color: '#AAA', textAlign: 'center', fontStyle: 'italic' }}>Geen orders gevonden</td></tr>
            ) : gefilterd.map((o, i) => {
              const voortgang = o.totaal_stappen > 0 ? Math.round((o.voltooide_stappen / o.totaal_stappen) * 100) : 0;
              const telaat = o.dagen_resterend < 0 && !AFGEWERKT_STATUSSEN.includes(o.status);
              const rij_bg = telaat ? '#FFF5F5' : i % 2 === 0 ? '#fff' : '#FAFAFA';

              return (
                <tr key={o.id} onClick={() => nav(`/orders/${o.id}`, { state: { from: '/orders' } })}
                  style={{ borderBottom: '1px solid #F0F0F0', cursor: 'pointer', background: rij_bg }}
                  onMouseEnter={e => e.currentTarget.style.background = '#EEF4FB'}
                  onMouseLeave={e => e.currentTarget.style.background = rij_bg}>

                  {/* Ordernummer met familie-kleur */}
                  <td style={{ padding: '9px 12px', whiteSpace: 'nowrap' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      {o.familie_kleur && (
                        <div style={{ width: 4, height: 20, borderRadius: 2, background: o.familie_kleur, flexShrink: 0 }} />
                      )}
                      <span style={{ fontWeight: 600, color: '#185FA5', fontSize: 12 }}>{o.ordernummer}</span>
                    </div>
                  </td>

                  <td style={{ padding: '9px 12px', color: '#333', whiteSpace: 'nowrap' }}>{o.klant}</td>

                  <td style={{ padding: '9px 12px', maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#555' }}>
                    {o.artikel_omschrijving}
                  </td>

                  <td style={{ padding: '9px 12px', color: '#555', textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {o.aantal}
                  </td>

                  <td style={{ padding: '9px 12px', color: '#555', whiteSpace: 'nowrap' }}
                      title={volledigeDatum(o.leverdatum)}>
                    {weekLabel(o.leverdatum)}
                  </td>

                  <td style={{ padding: '9px 12px', whiteSpace: 'nowrap' }}>
                    <span style={{
                      fontWeight: 600,
                      color: telaat ? '#A32D2D' : o.dagen_resterend <= 3 ? '#854F0B' : '#3B6D11',
                    }}>
                      {telaat
                        ? `${Math.abs(o.dagen_resterend)}d te laat`
                        : `${o.dagen_resterend}d`}
                    </span>
                  </td>

                  <td style={{ padding: '9px 12px' }}>
                    <span style={{
                      fontSize: 11, padding: '2px 8px', borderRadius: 99,
                      background: o.prioriteit === 'spoed' ? '#FDEDEC' : o.prioriteit === 'hoog' ? '#FDF3E3' : '#EAF3DE',
                      color: PRIO_CL[o.prioriteit] || '#555',
                      fontWeight: o.prioriteit === 'spoed' ? 700 : 400,
                    }}>
                      {o.prioriteit}
                    </span>
                  </td>

                  <td style={{ padding: '9px 12px' }}>
                    <span style={{
                      fontSize: 11, padding: '2px 8px', borderRadius: 99,
                      background: STATUS_BG[o.status] || '#F5F5F5',
                      color: STATUS_CL[o.status] || '#555',
                    }}>
                      {STATUS_NL[o.status] || o.status}
                    </span>
                  </td>

                  <td style={{ padding: '9px 12px' }}>
                    {o.materiaal_status && (
                      <span style={{
                        fontSize: 11, padding: '2px 8px', borderRadius: 99,
                        background: MAT_BG[o.materiaal_status] || '#F5F5F5',
                        color: MAT_CL[o.materiaal_status] || '#555',
                      }}>
                        {MAT_NL[o.materiaal_status] || o.materiaal_status}
                      </span>
                    )}
                  </td>

                  <td style={{ padding: '9px 12px', minWidth: 100 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <div style={{ flex: 1, height: 6, background: '#EEE', borderRadius: 99 }}>
                        <div style={{
                          width: `${voortgang}%`, height: 6, borderRadius: 99,
                          background: voortgang === 100 ? '#639922' : '#185FA5',
                          transition: 'width 0.3s',
                        }} />
                      </div>
                      <span style={{ fontSize: 11, color: '#888', minWidth: 30, textAlign: 'right' }}>{voortgang}%</span>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Footer */}
      <div style={{ marginTop: 10, fontSize: 12, color: '#888' }}>
        {gefilterd.length} order{gefilterd.length !== 1 ? 's' : ''} gevonden{orders.length !== gefilterd.length ? ` (van ${orders.length})` : ''}
      </div>
    </div>
  );
}
